import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  Users,
  CheckCircle,
  CheckCircle2,
  AlertCircle,
  TrendingUp,
  Plus,
  Loader2,
  PanelRightOpen,
  PanelRightClose,
  XCircle,
  FileText,
  Download,
  User,
  Phone,
  Mail,
  Sparkles,
} from "lucide-react";
import PaymentActions from "@/components/PaymentActions";

const formatDateKey = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getStatusLabel = (status) => {
  const labels = {
    booked: "Pending",
    pending: "Pending",
    completed: "Completed",
    cancelled: "Cancelled",
    no_show: "No Show",
  };
  return labels[status] || status;
};

const CalendarPage = () => {
  const { api, business, user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState("week");
  // Outer mode toggle: "calendar" (existing grid) | "schedule" (chronological list).
  // Remembered per-browser via localStorage; defaults to "calendar".
  const [viewMode, setViewMode] = useState(() => {
    const saved = localStorage.getItem("slotmate_calendar_view_mode");
    return saved === "schedule" ? "schedule" : "calendar";
  });
  useEffect(() => {
    localStorage.setItem("slotmate_calendar_view_mode", viewMode);
  }, [viewMode]);
  // Auto-select logged-in staff member if user is staff role
  const [selectedStaff, setSelectedStaff] = useState(() => {
    if (user?.role === "staff" && user?.id) {
      return user.id;
    }
    return "all";
  });
  const [showStats, setShowStats] = useState(() => {
    const saved = localStorage.getItem("slotmate_calendar_stats");
    return saved !== "false";
  });

  // Appointment details modal state
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  // Walk-in appointment modal state
  const [showWalkInModal, setShowWalkInModal] = useState(false);
  const [walkInData, setWalkInData] = useState({
    date: "",
    time: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    service_id: "",
    staff_id: "",
    notes: "",
  });
  const [savingWalkIn, setSavingWalkIn] = useState(false);

  // Drag and drop state
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);

  useEffect(() => {
    if (business?.id) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business, selectedDate, selectedStaff, view]);

  // Save stats toggle preference
  const toggleStats = () => {
    const newValue = !showStats;
    setShowStats(newValue);
    localStorage.setItem("slotmate_calendar_stats", newValue.toString());
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const startDate = new Date(selectedDate);
      const endDate = new Date(selectedDate);
      const weekStart = new Date(selectedDate);
      weekStart.setDate(weekStart.getDate() - weekStart.getDay());
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      if (view === "day") {
        // Just the selected day
      } else if (view === "week") {
        startDate.setDate(startDate.getDate() - startDate.getDay());
        endDate.setDate(startDate.getDate() + 6);
      } else {
        // Month view
        startDate.setDate(1);
        endDate.setMonth(endDate.getMonth() + 1);
        endDate.setDate(0);
      }

      // Stats always need the complete selected week, including in day/month views.
      if (weekStart < startDate) startDate.setTime(weekStart.getTime());
      if (weekEnd > endDate) endDate.setTime(weekEnd.getTime());

      const params = {
        start_date: formatDateKey(startDate),
        end_date: formatDateKey(endDate),
      };

      if (selectedStaff !== "all") {
        params.staff_id = selectedStaff;
      }

      const [appointmentsRes, staffRes, servicesRes] = await Promise.all([
        api.get(`/businesses/${business.id}/appointments`, { params }),
        user.role === "owner"
          ? api.get(`/businesses/${business.id}/staff`)
          : Promise.resolve({ data: [] }),
        api.get(`/businesses/${business.id}/services`),
      ]);

      setAppointments(appointmentsRes.data);
      setStaff(staffRes.data);
      setServices(servicesRes.data.filter((s) => s.is_active));
    } catch (error) {
      console.error("Failed to fetch calendar data:", error);
      toast.error("Failed to load calendar");
    } finally {
      setLoading(false);
    }
  };

  const navigateDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (view === "day") {
      newDate.setDate(newDate.getDate() + direction);
    } else if (view === "week") {
      newDate.setDate(newDate.getDate() + direction * 7);
    } else {
      newDate.setMonth(newDate.getMonth() + direction);
    }
    setSelectedDate(newDate);
  };

  const getWeekDays = () => {
    const days = [];
    const startOfWeek = new Date(selectedDate);
    startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(day.getDate() + i);
      days.push(day);
    }
    return days;
  };

  const getMonthDays = () => {
    const days = [];
    const firstDay = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      1,
    );
    const lastDay = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth() + 1,
      0,
    );

    const startPadding = firstDay.getDay();

    for (let i = startPadding - 1; i >= 0; i--) {
      const day = new Date(firstDay);
      day.setDate(day.getDate() - i - 1);
      days.push({ date: day, isCurrentMonth: false });
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({
        date: new Date(selectedDate.getFullYear(), selectedDate.getMonth(), i),
        isCurrentMonth: true,
      });
    }

    const endPadding = 42 - days.length;
    for (let i = 1; i <= endPadding; i++) {
      const day = new Date(lastDay);
      day.setDate(day.getDate() + i);
      days.push({ date: day, isCurrentMonth: false });
    }

    return days;
  };

  const getAppointmentsForDate = (date) => {
    const dateStr = formatDateKey(date);
    return appointments.filter(
      (a) => a.date === dateStr && a.status !== "cancelled",
    );
  };

  const timeSlots = [];
  // Build time slots from business hours (earliest open to latest close)
  const businessHours = business?.business_hours || [];
  let earliestOpen = 8;
  let latestClose = 18;
  if (businessHours.length > 0) {
    const openHours = businessHours.filter((h) => !h.is_closed);
    if (openHours.length > 0) {
      earliestOpen = Math.min(
        ...openHours.map((h) => parseInt(h.open_time.split(":")[0])),
      );
      latestClose = Math.max(
        ...openHours.map((h) => parseInt(h.close_time.split(":")[0])),
      );
    }
  }
  for (let i = earliestOpen; i <= latestClose; i++) {
    timeSlots.push(`${i.toString().padStart(2, "0")}:00`);
  }

  // Helper: get business hours for a specific date
  // business_hours uses 0=Monday..6=Sunday, JS Date.getDay() uses 0=Sunday..6=Saturday
  const getBusinessHoursForDate = (date) => {
    const jsDay = date.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
    const bizDay = jsDay === 0 ? 6 : jsDay - 1; // Convert to 0=Mon..6=Sun
    return businessHours.find((h) => h.day === bizDay) || null;
  };

  const isSlotClosed = (date, time) => {
    const hours = getBusinessHoursForDate(date);
    if (!hours) return false; // No hours set, allow all
    if (hours.is_closed) return true; // Day is closed
    const slotHour = parseInt(time.split(":")[0]);
    const openHour = parseInt(hours.open_time.split(":")[0]);
    const closeHour = parseInt(hours.close_time.split(":")[0]);
    return slotHour < openHour || slotHour >= closeHour;
  };

  const isDayClosed = (date) => {
    const hours = getBusinessHoursForDate(date);
    return hours?.is_closed === true;
  };

  const formatDateHeader = () => {
    if (view === "month") {
      return selectedDate.toLocaleDateString("en-US", {
        month: "long",
        year: "numeric",
      });
    }
    if (view === "week") {
      const weekDays = getWeekDays();
      const start = weekDays[0].toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
      const end = weekDays[6].toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return `${start} - ${end}`;
    }
    return selectedDate.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  const getStaffWorkingOnDate = (dateValue) => {
    if (!dateValue) return [];

    const [year, month, day] = dateValue.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    const scheduleDay = date.getDay() === 0 ? 6 : date.getDay() - 1;

    return staff.filter((staffMember) => {
      const schedule = (staffMember.schedules || []).find(
        (item) => item.day === scheduleDay,
      );
      return schedule?.is_available !== false && Boolean(schedule);
    });
  };

  const handleSlotClick = (date, time, staffId) => {
    if (isSlotClosed(date, time || "09:00")) {
      toast.error("Business is closed at this time");
      return;
    }
    if (isDayClosed(date)) {
      toast.error("Business is closed on this day");
      return;
    }

    // Check if the slot is in the past
    const slotDateTime = new Date(date);
    const [hours, minutes] = (time || "09:00").split(":").map(Number);
    slotDateTime.setHours(hours, minutes, 0, 0);
    const now = new Date();

    if (slotDateTime < now) {
      toast.warning("You are creating an appointment in the past", {
        duration: 4000,
      });
    }

    const dateStr = formatDateKey(date);
    const workingStaff = getStaffWorkingOnDate(dateStr);
    const requestedStaffIsWorking = workingStaff.some(
      (staffMember) => staffMember.id === staffId,
    );
    setWalkInData({
      date: dateStr,
      time: time || "09:00",
      customer_name: "",
      customer_phone: "",
      customer_email: "",
      service_id: services.length > 0 ? services[0].id : "",
      staff_id: requestedStaffIsWorking
        ? staffId
        : workingStaff[0]?.id || (user.role === "staff" ? user.id : ""),
      notes: "Walk-in appointment",
    });
    setShowWalkInModal(true);
  };

  const handleSaveWalkIn = async () => {
    if (!walkInData.customer_name || !walkInData.customer_phone) {
      toast.error("Please enter customer name and phone number");
      return;
    }

    if (!walkInData.service_id) {
      toast.error("Please select a service");
      return;
    }

    if (staff.length > 0 && !walkInData.staff_id) {
      toast.error("No staff are working on the selected day");
      return;
    }

    setSavingWalkIn(true);
    try {
      await api.post(`/businesses/${business.id}/appointments/walk-in`, {
        date: walkInData.date,
        time: walkInData.time,
        customer_name: walkInData.customer_name,
        customer_phone: walkInData.customer_phone,
        customer_email: walkInData.customer_email || null,
        service_id: walkInData.service_id,
        staff_id: walkInData.staff_id || null,
        notes: walkInData.notes,
      });

      toast.success("Walk-in appointment created!");
      setShowWalkInModal(false);
      fetchData();
    } catch (error) {
      console.error("Failed to create walk-in:", error);
      toast.error(
        error.response?.data?.detail || "Failed to create appointment",
      );
    } finally {
      setSavingWalkIn(false);
    }
  };

  const handleDragStart = (e, appointment) => {
    setDraggedAppointment(appointment);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, date, time) => {
    e.preventDefault();
    const dateStr = date.toISOString().split("T")[0];
    setDragOverSlot({ date: dateStr, time });
  };

  const handleDragLeave = () => {
    setDragOverSlot(null);
  };

  const handleDrop = async (e, date, time) => {
    e.preventDefault();
    setDragOverSlot(null);

    if (!draggedAppointment) return;

    if (isSlotClosed(date, time)) {
      toast.error("Cannot reschedule to a closed time slot");
      setDraggedAppointment(null);
      return;
    }

    const newDate = date.toISOString().split("T")[0];

    if (
      draggedAppointment.date === newDate &&
      draggedAppointment.time === time
    ) {
      setDraggedAppointment(null);
      return;
    }

    try {
      await api.put(
        `/appointments/${draggedAppointment.id}/reschedule?date=${newDate}&time=${time}`,
      );
      toast.success("Appointment rescheduled!");
      fetchData();
    } catch (error) {
      console.error("Failed to reschedule:", error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === "string") {
        toast.error(errorMsg);
      } else if (Array.isArray(errorMsg)) {
        toast.error(errorMsg.map((e) => e.msg).join(", "));
      } else {
        toast.error("Failed to reschedule appointment");
      }
    } finally {
      setDraggedAppointment(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedAppointment(null);
    setDragOverSlot(null);
  };

  // --- Appointment Detail Modal Functions ---
  const handleEventClick = (e, appt) => {
    e.stopPropagation();
    setSelectedAppointment(appt);
    setDetailsOpen(true);
  };

  const getStatusBadge = (status) => {
    const styles = {
      booked: "bg-amber-50 text-amber-700 border border-amber-200",
      pending: "bg-amber-50 text-amber-700 border border-amber-200",
      completed: "bg-green-50 text-green-700 border border-green-200",
      cancelled: "bg-red-50 text-red-700 border border-red-200",
      no_show: "bg-rose-50 text-rose-700 border border-rose-200",
    };
    return `inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${styles[status] || styles.pending}`;
  };

  const updateAppointmentStatus = async (appointmentId, status) => {
    try {
      await api.put(
        `/businesses/${business.id}/appointments/${appointmentId}`,
        { status },
      );
      toast.success(`Appointment ${status}`);
      fetchData();
      if (selectedAppointment?.id === appointmentId) {
        setSelectedAppointment((prev) => ({ ...prev, status }));
      }
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    }
  };

  // For terminal actions (complete / no_show / cancel): update status then close modal
  const handleStatusAndClose = async (appointmentId, status) => {
    const labels = {
      completed: "Appointment marked as complete",
      no_show: "Marked as no-show",
      cancelled: "Appointment cancelled",
    };
    try {
      await api.put(
        `/businesses/${business.id}/appointments/${appointmentId}`,
        { status },
      );
      toast.success(labels[status] || `Appointment ${status}`);
      setDetailsOpen(false);
      fetchData();
    } catch (error) {
      console.error("Failed to update appointment:", error);
      toast.error("Failed to update appointment");
    }
  };

  const updateInternalNotes = async (appointmentId, notes) => {
    try {
      await api.put(
        `/businesses/${business.id}/appointments/${appointmentId}`,
        { internal_notes: notes },
      );
      toast.success("Notes saved");
    } catch (error) {
      console.error("Failed to save notes:", error);
      toast.error("Failed to save notes");
    }
  };

  const generateInvoice = async (appointmentId) => {
    setGeneratingInvoice(true);
    try {
      const response = await api.post(
        `/businesses/${business.id}/invoices`,
        { appointment_id: appointmentId, tax_rate: 0 },
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice-${appointmentId.slice(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Invoice downloaded");
    } catch (error) {
      console.error("Failed to generate invoice:", error);
      toast.error("Failed to generate invoice");
    } finally {
      setGeneratingInvoice(false);
    }
  };

  // Status colors — soft pastel backgrounds with colored left accent bar
  // Completed = soft green, booked/pending = soft amber, no-shows = soft rose.
  const getAppointmentColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 hover:bg-green-200 text-green-800 border-l-4 border-l-green-500";
      case "no_show":
        return "bg-rose-100 hover:bg-rose-200 text-rose-800 border-l-4 border-l-rose-400";
      case "booked":
        return "bg-amber-50 hover:bg-amber-100 text-amber-800 border-l-4 border-l-amber-400";
      case "pending":
        return "bg-amber-50 hover:bg-amber-100 text-amber-800 border-l-4 border-l-amber-400";
      default:
        return "bg-slate-100 hover:bg-slate-200 text-slate-700 border-l-4 border-l-slate-400";
    }
  };

  // --- Simple Side-by-Side Overlap Layout ---
  // Overlapping appointments are rendered side-by-side (Google Calendar style)
  const layoutAppointments = (appts) => {
    const toH = (t) => {
      const [h, m] = t.split(":").map(Number);
      return h + m / 60;
    };

    const items = appts.map((a) => ({
      appt: a,
      start: toH(a.time),
      end: toH(a.end_time || `${parseInt(a.time) + 1}:00`),
      col: 0,
      totalCols: 1,
    }));

    // Sort by start time
    items.sort((a, b) => a.start - b.start);

    // Assign columns greedily
    const cols = [];
    items.forEach((item) => {
      let placed = false;
      for (let c = 0; c < cols.length; c++) {
        if (item.start >= cols[c]) {
          item.col = c;
          cols[c] = item.end;
          placed = true;
          break;
        }
      }
      if (!placed) {
        item.col = cols.length;
        cols.push(item.end);
      }
    });

    // Determine totalCols for each item
    items.forEach((item) => {
      let maxCol = item.col;
      items.forEach((other) => {
        if (
          other !== item &&
          other.start < item.end &&
          other.end > item.start
        ) {
          maxCol = Math.max(maxCol, other.col);
        }
      });
      item.totalCols = maxCol + 1;
    });

    return items;
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Stats calculations
  // Today's total includes every status, including cancelled and no-show.
  const todayDate = formatDateKey(new Date());
  const todayAppointments = appointments.filter((a) => a.date === todayDate);
  const completedToday = todayAppointments.filter(
    (a) => a.status === "completed",
  ).length;
  const cancelledToday = todayAppointments.filter(
    (a) => a.status === "cancelled",
  ).length;
  const noShowToday = todayAppointments.filter(
    (a) => a.status === "no_show",
  ).length;
  const pendingToday = todayAppointments.filter(
    (a) => a.status === "booked" || a.status === "pending",
  ).length;

  const weekDates = new Set(getWeekDays().map((day) => formatDateKey(day)));
  const weekAppointments = appointments.filter((a) => weekDates.has(a.date));
  const completedWeek = weekAppointments.filter(
    (a) => a.status === "completed",
  ).length;
  const cancelledWeek = weekAppointments.filter(
    (a) => a.status === "cancelled",
  ).length;
  const noShowWeek = weekAppointments.filter(
    (a) => a.status === "no_show",
  ).length;
  const pendingWeek = weekAppointments.filter(
    (a) => a.status === "booked" || a.status === "pending",
  ).length;

  const weeklyPerformance = getWeekDays().map((day) => ({
    day: day.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
    count: getAppointmentsForDate(day).length,
    isToday: day.toDateString() === new Date().toDateString(),
  }));

  const maxCount = Math.max(...weeklyPerformance.map((d) => d.count), 1);
  const availableWalkInStaff = getStaffWorkingOnDate(walkInData.date);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 animate-spin text-sky-600" />
            <p className="text-sm text-slate-500 font-medium">
              Loading calendar...
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div
        className="flex flex-col lg:flex-row gap-4 lg:gap-6"
        data-testid="calendar-page"
      >
        {/* Main Calendar Section */}
        <div className="flex-1 min-w-0">
          {/* Header - Single Row */}
          <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
            {/* Navigation */}
            <div className="flex w-full min-w-0 items-center justify-between gap-1 sm:w-auto sm:justify-start">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigateDate(-1)}
                className="rounded-lg hover:bg-slate-100 h-8 w-8 transition-colors"
                data-testid="calendar-prev"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </Button>
              <h2 className="min-w-0 flex-1 text-sm sm:flex-none sm:text-lg font-semibold text-slate-900 text-center px-1">
                {formatDateHeader()}
              </h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDate(new Date())}
                className="text-sm font-medium text-sky-600 hover:text-sky-700 hover:bg-sky-50 rounded-lg px-2 h-8"
              >
                Today
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigateDate(1)}
                className="rounded-lg hover:bg-slate-100 h-8 w-8 transition-colors"
                data-testid="calendar-next"
              >
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </Button>
            </div>

            {/* Status Legend - Solid colors, lighter */}
            <div className="hidden sm:flex items-center gap-4 px-2">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-green-400"></div>
                <span className="text-xs text-slate-500">Completed</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-amber-400"></div>
                <span className="text-xs text-slate-500">Pending</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-rose-400"></div>
                <span className="text-xs text-slate-500">No Shows</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto sm:flex-nowrap">
              {/* Outer mode: Calendar grid vs Schedule list */}
              <div
                className="inline-flex items-center bg-slate-100 rounded-lg p-0.5 shadow-inner"
                data-testid="view-mode-toggle"
              >
                {[
                  { key: "calendar", label: "Calendar" },
                  { key: "schedule", label: "Schedule" },
                ].map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setViewMode(item.key)}
                    className={`px-2 sm:px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${
                      viewMode === item.key
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid={`view-mode-${item.key}`}
                    aria-pressed={viewMode === item.key}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              {staff.length > 0 && (
                <Select value={selectedStaff} onValueChange={setSelectedStaff}>
                  <SelectTrigger
                    className="min-w-0 flex-1 sm:w-[130px] sm:flex-none bg-white border-slate-200 rounded-lg text-sm h-9 shadow-sm hover:border-slate-300 transition-colors"
                    data-testid="calendar-staff-filter"
                  >
                    <Users className="w-4 h-4 mr-1.5 text-slate-400" />
                    <SelectValue placeholder="All Staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {staff.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name || s.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* Segmented Control */}
              <div className="inline-flex w-full items-center bg-slate-100 rounded-lg p-0.5 shadow-inner sm:w-auto">
                {[
                  { key: "day", label: "Day" },
                  { key: "week", label: "Week" },
                  { key: "month", label: "Month" },
                ].map((item) => (
                  <button
                    key={item.key}
                    onClick={() => setView(item.key)}
                    className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200 sm:flex-none ${
                      view === item.key
                        ? "bg-white text-slate-900 shadow-sm"
                        : "text-slate-500 hover:text-slate-700"
                    }`}
                    data-testid={`view-${item.key}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {/* Stats toggle - Only on lg screens */}
              <Button
                variant="outline"
                size="icon"
                onClick={toggleStats}
                className="hidden lg:flex rounded-lg border-slate-200 hover:bg-slate-50 h-9 w-9 shadow-sm"
                title={showStats ? "Hide Stats" : "Show Stats"}
                data-testid="toggle-stats-btn"
              >
                {showStats ? (
                  <PanelRightClose className="w-4 h-4 text-slate-600" />
                ) : (
                  <PanelRightOpen className="w-4 h-4 text-slate-600" />
                )}
              </Button>
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {/* Week View */}
            {viewMode === "calendar" && view === "week" && (
              <div className="overflow-x-auto">
                {/* Single scrollable container — header and body share identical width so columns always align */}
                <div
                  className="min-w-[700px] max-h-[680px] sm:max-h-[760px] overflow-y-auto"
                  style={{ scrollbarGutter: "stable" }}
                >
                  {/* Sticky header inside the scroll container */}
                  <div className="grid grid-cols-8 border-b border-slate-200 bg-slate-50/80 backdrop-blur-sm sticky top-0 z-20">
                    <div className="p-3 text-xs font-semibold text-slate-400 uppercase tracking-wider border-r border-slate-200"></div>
                    {getWeekDays().map((day) => {
                      const isToday =
                        day.toDateString() === new Date().toDateString();
                      const dayName = day.toLocaleDateString("en-US", {
                        weekday: "short",
                      });
                      const dayClosed = isDayClosed(day);
                      return (
                        <div
                          key={day.toISOString()}
                          className={`p-3 text-center border-r border-slate-200 last:border-r-0 transition-colors ${dayClosed ? "bg-slate-100/50" : ""}`}
                        >
                          <div
                            className={`text-xs font-semibold uppercase tracking-wider mb-1 ${isToday ? "text-sky-600" : "text-slate-400"}`}
                          >
                            {dayName}
                          </div>
                          <div
                            className={`text-xl font-bold inline-flex items-center justify-center w-9 h-9 rounded-full transition-all ${
                              isToday
                                ? "bg-sky-600 text-white shadow-sm"
                                : dayClosed
                                  ? "text-slate-300"
                                  : "text-slate-800"
                            }`}
                          >
                            {day.getDate()}
                          </div>
                          {dayClosed && (
                            <div className="text-[10px] text-slate-400 font-medium mt-1 uppercase tracking-wide">
                              Closed
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Grid body */}
                  <div>
                    {/* Row height constant — must match ROW_H below */}
                    <div className="grid grid-cols-8">
                      {/* Time column */}
                      <div className="border-r border-slate-200 bg-slate-50/30">
                        {timeSlots.map((time, idx) => (
                          <div
                            key={time}
                            className="h-[60px] p-2 text-xs font-medium text-slate-400 text-right pr-3 border-b border-slate-200 last:border-b-0 flex items-start justify-end"
                          >
                            <span className="mt-[-6px]">{time}</span>
                          </div>
                        ))}
                      </div>
                      {/* One column per day */}
                      {getWeekDays().map((day) => {
                        const dateStr = day.toISOString().split("T")[0];
                        const ROW_H = 60; // px — must match h-[60px] above
                        const firstHour =
                          timeSlots.length > 0
                            ? parseInt(timeSlots[0].split(":")[0])
                            : 0;
                        const dayAppts = getAppointmentsForDate(day);
                        return (
                          <div
                            key={day.toISOString()}
                            className="relative border-r border-slate-200 last:border-r-0"
                          >
                            {/* Background slot rows for click/drop targets */}
                            {timeSlots.map((time, idx) => {
                              const closed = isSlotClosed(day, time);
                              const isDropTarget =
                                dragOverSlot?.date === dateStr &&
                                dragOverSlot?.time === time;
                              return (
                                <div
                                  key={time}
                                  className={`h-[60px] border-b border-slate-200 last:border-b-0 transition-all duration-150 ${
                                    closed
                                      ? "bg-slate-100/60 cursor-not-allowed"
                                      : isDropTarget
                                        ? "bg-sky-100 ring-2 ring-sky-300 ring-inset"
                                        : "cursor-pointer hover:bg-sky-50/50 bg-white"
                                  }`}
                                  onClick={() => handleSlotClick(day, time)}
                                  onDragOver={(e) =>
                                    !closed && handleDragOver(e, day, time)
                                  }
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) =>
                                    closed
                                      ? e.preventDefault()
                                      : handleDrop(e, day, time)
                                  }
                                  data-testid={`slot-${dateStr}-${time}`}
                                >
                                  {closed && (
                                    <div className="h-full flex items-center justify-center">
                                      <span className="text-xs text-slate-300 select-none font-medium">
                                        Closed
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            {/* Side-by-side appointment blocks */}
                            {layoutAppointments(dayAppts).map(
                              ({ appt, col, totalCols }) => {
                                const [sh, sm2] = appt.time
                                  .split(":")
                                  .map(Number);
                                const endStr = appt.end_time || `${sh + 1}:00`;
                                const [eh, em2] = endStr.split(":").map(Number);
                                const topPx =
                                  (sh - firstHour + sm2 / 60) * ROW_H;
                                const heightPx = Math.max(
                                  (eh - sh + (em2 - sm2) / 60) * ROW_H - 4,
                                  ROW_H - 4,
                                );

                                // Side-by-side columns
                                const GAP = 2;
                                const PADDING = 3;
                                const colW = `calc((100% - ${GAP * (totalCols - 1)}px - ${PADDING * 2}px) / ${totalCols})`;
                                const colLeft = `calc(${PADDING}px + (100% - ${GAP * (totalCols - 1)}px - ${PADDING * 2}px) / ${totalCols} * ${col} + ${GAP * col}px)`;

                                return (
                                  <div
                                    key={appt.id}
                                    draggable
                                    onDragStart={(e) =>
                                      handleDragStart(e, appt)
                                    }
                                    onDragEnd={handleDragEnd}
                                    className={`${getAppointmentColor(appt.status)} absolute rounded-lg p-1.5 cursor-grab active:cursor-grabbing shadow hover:shadow-sm transition-all duration-150`}
                                    style={{
                                      top: topPx + 2 + "px",
                                      height: heightPx + "px",
                                      left: colLeft,
                                      width: colW,
                                      zIndex: 10,
                                    }}
                                    onClick={(e) => handleEventClick(e, appt)}
                                    title={`${appt.time} - ${appt.customer_name}\n${appt.service_name}`}
                                    data-testid={`calendar-event-${appt.id}`}
                                  >
                                    <div className="text-[10px] font-semibold opacity-70">
                                      {appt.time}
                                    </div>
                                    <div className="text-xs font-semibold truncate leading-tight">
                                      {appt.customer_name}
                                    </div>
                                    <div className="text-[10px] opacity-60 truncate">
                                      {appt.service_name}
                                    </div>
                                  </div>
                                );
                              },
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Day View - Staff Columns */}
            {viewMode === "calendar" && view === "day" && (
              <>
                {(() => {
                  const ROW_H = 60;
                  const firstHour =
                    timeSlots.length > 0
                      ? parseInt(timeSlots[0].split(":")[0])
                      : 0;
                  const dateStr = selectedDate.toISOString().split("T")[0];
                  const dayAppts = getAppointmentsForDate(selectedDate);

                  // Get day of week (0=Monday...6=Sunday for staff schedules)
                  const dayOfWeek =
                    selectedDate.getDay() === 0 ? 6 : selectedDate.getDay() - 1;

                  // Build staff columns - include "Unassigned" if there are appointments without staff_id.
                  // Hide staff who are not rostered to work this day AND have no appointments
                  // (requirement: keep working staff + staff with appointments only).
                  const hasUnassigned = dayAppts.some((a) => !a.staff_id);
                  const isStaffWorkingToday = (s) => {
                    const schedules = s.schedules || [];
                    const sched = schedules.find(
                      (sch) => sch.day === dayOfWeek,
                    );
                    return !!(sched && sched.is_available !== false);
                  };
                  const staffWithApptsToday = new Set(
                    dayAppts
                      .map((a) => a.staff_id)
                      .filter((id) => !!id),
                  );
                  const visibleStaff = staff.filter(
                    (s) =>
                      isStaffWorkingToday(s) || staffWithApptsToday.has(s.id),
                  );
                  const staffColumns = [
                    ...visibleStaff.map((s) => ({
                      id: s.id,
                      name: s.name || s.email?.split("@")[0] || "Staff",
                      email: s.email,
                      schedules: s.schedules || [], // Staff's specific schedules array
                    })),
                    ...(hasUnassigned
                      ? [
                          {
                            id: null,
                            name: "Unassigned",
                            email: null,
                            schedules: [],
                          },
                        ]
                      : []),
                  ];

                  // If no staff, show single column
                  const showStaffColumns = staffColumns.length > 0;
                  const hasMultipleStaffColumns = staffColumns.length > 1;

                  // Get appointments for a specific staff
                  const getStaffAppointments = (staffId) => {
                    if (staffId === null) {
                      return dayAppts.filter((a) => !a.staff_id);
                    }
                    return dayAppts.filter((a) => a.staff_id === staffId);
                  };

                  // Get staff schedule for current day
                  const getStaffDaySchedule = (staffMember) => {
                    if (
                      !staffMember.schedules ||
                      staffMember.schedules.length === 0
                    )
                      return null;
                    return staffMember.schedules.find(
                      (s) => s.day === dayOfWeek,
                    );
                  };

                  // Check if staff works on this day (Away = no schedule or is_available=false)
                  const isStaffAway = (staffMember) => {
                    if (!staffMember.id) return false; // Unassigned column is never "away"
                    const schedule = getStaffDaySchedule(staffMember);
                    // If no schedule for this day, or is_available is explicitly false
                    if (!schedule) return true; // No schedule = Away
                    return schedule.is_available === false;
                  };

                  // Check if staff is available at a specific time slot
                  const isStaffSlotAvailable = (staffMember, time) => {
                    if (!staffMember.id) return true; // Unassigned column always available

                    const schedule = getStaffDaySchedule(staffMember);
                    if (!schedule || !schedule.is_available) return false; // Staff away

                    const slotHour = parseInt(time.split(":")[0]);
                    const slotMin = parseInt(time.split(":")[1]) || 0;
                    const slotTotalMin = slotHour * 60 + slotMin;

                    const startParts = (schedule.start_time || "09:00").split(
                      ":",
                    );
                    const endParts = (schedule.end_time || "17:00").split(":");
                    const startMin =
                      parseInt(startParts[0]) * 60 +
                      parseInt(startParts[1] || 0);
                    const endMin =
                      parseInt(endParts[0]) * 60 + parseInt(endParts[1] || 0);

                    return slotTotalMin >= startMin && slotTotalMin < endMin;
                  };

                  // Colors for staff avatars
                  const avatarColors = [
                    "from-sky-400 to-sky-600",
                    "from-emerald-400 to-emerald-600",
                    "from-amber-400 to-amber-600",
                    "from-rose-400 to-rose-600",
                    "from-violet-400 to-violet-600",
                    "from-cyan-400 to-cyan-600",
                    "from-orange-400 to-orange-600",
                    "from-pink-400 to-pink-600",
                  ];

                  return (
                    <div className="w-full max-w-full overflow-x-auto">
                      <div
                        className={`w-full ${
                          hasMultipleStaffColumns
                            ? "min-w-[600px]"
                            : "min-w-0"
                        }`}
                      >
                        {/* Grid Body */}
                        <div className="max-h-[500px] sm:max-h-[600px] overflow-y-auto">
                          {/* Staff Header Row — inside scroll container for alignment */}
                          {showStaffColumns && (
                            <div className="flex border-b border-slate-200 bg-slate-50/50 sticky top-0 z-20">
                              {/* Time column header */}
                              <div className="w-16 sm:w-20 flex-shrink-0 border-r border-slate-200 p-3 bg-slate-50/50">
                                <div
                                  className={`text-center ${
                                    selectedDate.toDateString() ===
                                    new Date().toDateString()
                                      ? "text-sky-600"
                                      : "text-slate-500"
                                  }`}
                                >
                                  <div className="text-xs font-medium uppercase">
                                    {selectedDate.toLocaleDateString("en-US", {
                                      weekday: "short",
                                    })}
                                  </div>
                                  <div
                                    className={`text-xl font-bold mt-1 ${
                                      selectedDate.toDateString() ===
                                      new Date().toDateString()
                                        ? "bg-sky-600 text-white w-9 h-9 rounded-full flex items-center justify-center mx-auto"
                                        : ""
                                    }`}
                                  >
                                    {selectedDate.getDate()}
                                  </div>
                                </div>
                              </div>
                              {/* Staff columns headers */}
                              {staffColumns.map((s, idx) => {
                                const away = isStaffAway(s);
                                return (
                                  <div
                                    key={s.id || "unassigned"}
                                    className={`flex-1 min-w-[120px] border-r border-slate-200 last:border-r-0 p-3 bg-slate-50/50 ${away ? "bg-slate-100" : ""}`}
                                  >
                                    <div className="flex flex-col items-center">
                                      <div
                                        className={`w-10 h-10 rounded-full bg-gradient-to-br ${avatarColors[idx % avatarColors.length]} flex items-center justify-center text-white text-sm font-semibold shadow-sm ${away ? "opacity-50" : ""}`}
                                      >
                                        {s.name?.[0]?.toUpperCase() || "?"}
                                      </div>
                                      <span
                                        className={`text-xs font-medium mt-1.5 truncate max-w-[100px] ${away ? "text-slate-400" : "text-slate-700"}`}
                                      >
                                        {s.name}
                                      </span>
                                      {away && (
                                        <span className="text-[10px] text-slate-400 font-medium">
                                          Away
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          <div className="flex">
                            {/* Time labels column */}
                            <div className="w-16 sm:w-20 flex-shrink-0 border-r border-slate-200 bg-slate-50/30">
                              {timeSlots.map((time) => (
                                <div
                                  key={time}
                                  className={`h-[60px] p-2 text-xs font-medium border-b border-slate-200 last:border-b-0 flex items-start justify-end pr-3 ${isSlotClosed(selectedDate, time) ? "text-slate-300" : "text-slate-400"}`}
                                >
                                  <span className="mt-[-6px]">{time}</span>
                                </div>
                              ))}
                            </div>

                            {/* Staff columns with appointments */}
                            {showStaffColumns ? (
                              staffColumns.map((s) => {
                                const staffAppts = getStaffAppointments(s.id);
                                const away = isStaffAway(s);
                                return (
                                  <div
                                    key={s.id || "unassigned"}
                                    className="flex-1 min-w-[120px] relative border-r border-slate-200 last:border-r-0"
                                  >
                                    {/* Background slot rows */}
                                    {timeSlots.map((time) => {
                                      const businessClosed = isSlotClosed(
                                        selectedDate,
                                        time,
                                      );
                                      const staffAvailable =
                                        isStaffSlotAvailable(s, time);
                                      const isDropTarget =
                                        dragOverSlot?.date === dateStr &&
                                        dragOverSlot?.time === time;

                                      // Staff away entire day
                                      if (away) {
                                        return (
                                          <div
                                            key={time}
                                            className="h-[60px] border-b border-slate-200 last:border-b-0 bg-slate-100 cursor-not-allowed"
                                          >
                                            <div className="h-full flex items-center justify-center">
                                              <span className="text-xs text-slate-300 select-none font-medium">
                                                Away
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // Staff not available at this time (shorter hours)
                                      if (!staffAvailable && !businessClosed) {
                                        return (
                                          <div
                                            key={time}
                                            className="h-[60px] border-b border-slate-200 last:border-b-0 bg-slate-50 cursor-not-allowed"
                                          >
                                            <div className="h-full flex items-center justify-center">
                                              <span className="text-[10px] text-slate-300 select-none">
                                                N/A
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // Business closed
                                      if (businessClosed) {
                                        return (
                                          <div
                                            key={time}
                                            className="h-[60px] border-b border-slate-200 last:border-b-0 bg-slate-100/60 cursor-not-allowed"
                                          >
                                            <div className="h-full flex items-center justify-center">
                                              <span className="text-xs text-slate-300 select-none font-medium">
                                                Closed
                                              </span>
                                            </div>
                                          </div>
                                        );
                                      }

                                      // Available slot
                                      return (
                                        <div
                                          key={time}
                                          className={`h-[60px] border-b border-slate-200 last:border-b-0 transition-all duration-150 ${
                                            isDropTarget
                                              ? "bg-sky-100 ring-2 ring-sky-300 ring-inset"
                                              : "cursor-pointer hover:bg-sky-50/50 bg-white"
                                          }`}
                                          onClick={() =>
                                            handleSlotClick(
                                              selectedDate,
                                              time,
                                              s.id,
                                            )
                                          }
                                          onDragOver={(e) =>
                                            handleDragOver(
                                              e,
                                              selectedDate,
                                              time,
                                            )
                                          }
                                          onDragLeave={handleDragLeave}
                                          onDrop={(e) =>
                                            handleDrop(e, selectedDate, time)
                                          }
                                        />
                                      );
                                    })}

                                    {/* Appointments for this staff */}
                                    {staffAppts.map((appt) => {
                                      const [sh, sm2] = appt.time
                                        .split(":")
                                        .map(Number);
                                      const endStr =
                                        appt.end_time || `${sh + 1}:00`;
                                      const [eh, em2] = endStr
                                        .split(":")
                                        .map(Number);
                                      const topPx =
                                        (sh - firstHour + sm2 / 60) * ROW_H;
                                      const heightPx = Math.max(
                                        (eh - sh + (em2 - sm2) / 60) * ROW_H -
                                          4,
                                        ROW_H - 4,
                                      );

                                      return (
                                        <div
                                          key={appt.id}
                                          draggable
                                          onDragStart={(e) =>
                                            handleDragStart(e, appt)
                                          }
                                          onDragEnd={handleDragEnd}
                                          className={`${getAppointmentColor(appt.status)} absolute left-1 right-1 rounded-lg p-2 cursor-grab active:cursor-grabbing shadow hover:shadow-sm transition-all duration-150`}
                                          style={{
                                            top: topPx + 2 + "px",
                                            height: heightPx + "px",
                                            zIndex: 10,
                                          }}
                                          onClick={(e) =>
                                            handleEventClick(e, appt)
                                          }
                                          data-testid={`calendar-event-${appt.id}`}
                                        >
                                          <div className="text-[10px] font-medium opacity-70">
                                            {appt.time} - {appt.end_time}
                                          </div>
                                          <div className="text-xs font-semibold truncate leading-tight">
                                            {appt.customer_name}
                                          </div>
                                          <div className="text-[10px] opacity-60 truncate">
                                            {appt.service_name}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              })
                            ) : (
                              /* Fallback: Single column if no staff */
                              <div className="flex-1 relative">
                                {timeSlots.map((time) => {
                                  const closed = isSlotClosed(
                                    selectedDate,
                                    time,
                                  );
                                  return (
                                    <div
                                      key={time}
                                      className={`h-[60px] border-b border-slate-200 last:border-b-0 ${
                                        closed
                                          ? "bg-slate-100/60"
                                          : "bg-white hover:bg-sky-50/50 cursor-pointer"
                                      }`}
                                      onClick={() =>
                                        handleSlotClick(selectedDate, time)
                                      }
                                    >
                                      {closed && (
                                        <div className="h-full flex items-center pl-4">
                                          <span className="text-xs text-slate-300">
                                            Closed
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                                {dayAppts.map((appt) => {
                                  const [sh, sm2] = appt.time
                                    .split(":")
                                    .map(Number);
                                  const endStr =
                                    appt.end_time || `${sh + 1}:00`;
                                  const [eh, em2] = endStr
                                    .split(":")
                                    .map(Number);
                                  const topPx =
                                    (sh - firstHour + sm2 / 60) * ROW_H;
                                  const heightPx = Math.max(
                                    (eh - sh + (em2 - sm2) / 60) * ROW_H - 4,
                                    ROW_H - 4,
                                  );

                                  return (
                                    <div
                                      key={appt.id}
                                      className={`${getAppointmentColor(appt.status)} absolute left-2 right-2 rounded-lg p-2 shadow-sm`}
                                      style={{
                                        top: topPx + 2 + "px",
                                        height: heightPx + "px",
                                        zIndex: 10,
                                      }}
                                      onClick={(e) => handleEventClick(e, appt)}
                                    >
                                      <div className="text-[10px] opacity-70">
                                        {appt.time} - {appt.end_time}
                                      </div>
                                      <div className="text-xs font-semibold truncate">
                                        {appt.customer_name}
                                      </div>
                                      <div className="text-[10px] opacity-60 truncate">
                                        {appt.service_name}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {/* Schedule View — chronological list of appointments grouped by day */}
            {viewMode === "schedule" && (
              <div
                className="p-3 sm:p-5 bg-slate-50/30"
                data-testid="schedule-view"
              >
                {(() => {
                  // Determine date range based on the inner view (day/week/month)
                  const rangeStart = new Date(selectedDate);
                  rangeStart.setHours(0, 0, 0, 0);
                  const rangeEnd = new Date(selectedDate);
                  rangeEnd.setHours(23, 59, 59, 999);
                  if (view === "week") {
                    rangeStart.setDate(
                      rangeStart.getDate() - rangeStart.getDay(),
                    );
                    rangeEnd.setTime(rangeStart.getTime());
                    rangeEnd.setDate(rangeEnd.getDate() + 6);
                    rangeEnd.setHours(23, 59, 59, 999);
                  } else if (view === "month") {
                    rangeStart.setDate(1);
                    rangeEnd.setMonth(rangeEnd.getMonth() + 1);
                    rangeEnd.setDate(0);
                    rangeEnd.setHours(23, 59, 59, 999);
                  }

                  // Filter + sort appointments
                  const within = appointments
                    .filter((a) => {
                      if (!a.date) return false;
                      const [y, m, d] = a.date.split("-").map(Number);
                      const dt = new Date(y, m - 1, d);
                      return dt >= rangeStart && dt <= rangeEnd;
                    })
                    .filter((a) =>
                      selectedStaff === "all"
                        ? true
                        : a.staff_id === selectedStaff,
                    )
                    .sort((a, b) => {
                      if (a.date !== b.date) return a.date < b.date ? -1 : 1;
                      return (a.time || "") < (b.time || "") ? -1 : 1;
                    });

                  if (within.length === 0) {
                    return (
                      <div
                        className="py-16 text-center text-slate-400"
                        data-testid="schedule-empty"
                      >
                        <p className="text-sm">
                          No appointments in this {view === "day" ? "day" : view}.
                        </p>
                      </div>
                    );
                  }

                  // Group by date
                  const groups = {};
                  within.forEach((a) => {
                    (groups[a.date] = groups[a.date] || []).push(a);
                  });

                  // Pastel palette mapped to appointment STATUS so owners
                  // can recognise outcomes at a glance.
                  const STATUS_PALETTE = {
                    completed: { bg: "#DCFCE7", border: "#86EFAC" },     // green
                    confirmed: { bg: "#E0F2FE", border: "#7DD3FC" },     // sky
                    pending:   { bg: "#FEF3C7", border: "#FCD34D" },     // amber
                    cancelled: { bg: "#FEE2E2", border: "#FCA5A5" },     // red
                    no_show:   { bg: "#FEE2E2", border: "#FCA5A5" },     // red
                  };
                  const NEUTRAL = { bg: "#F1F5F9", border: "#CBD5E1" };
                  const colorFor = (status) =>
                    STATUS_PALETTE[status] || NEUTRAL;
                  const STATUS_DOT = {
                    completed: "bg-green-400",
                    pending: "bg-amber-400",
                    confirmed: "bg-sky-400",
                    cancelled: "bg-rose-400",
                    no_show: "bg-rose-400",
                  };
                  const formatTime = (t) => {
                    if (!t) return "";
                    const [hh, mm] = t.split(":").map(Number);
                    const ampm = hh >= 12 ? "PM" : "AM";
                    const h12 = hh % 12 === 0 ? 12 : hh % 12;
                    return `${h12}:${String(mm || 0).padStart(2, "0")} ${ampm}`;
                  };
                  const staffName = (id) => {
                    if (!id) return "Unassigned";
                    const s = staff.find((x) => x.id === id);
                    return s?.name || s?.email?.split("@")[0] || "Staff";
                  };

                  const sortedDates = Object.keys(groups).sort();

                  // For each day, group its appointments by staff_id and
                  // build one column per staff who actually has appointments.
                  const staffMeta = (id) => {
                    if (!id) {
                      return {
                        id: null,
                        name: "Unassigned",
                        title: "",
                        avatar_url: "",
                      };
                    }
                    const s = staff.find((x) => x.id === id);
                    return {
                      id,
                      name: s?.name || s?.email?.split("@")[0] || "Staff",
                      title: s?.title || "Beauty Therapist",
                      avatar_url: s?.avatar_url || "",
                    };
                  };

                  const STAFF_AVATAR_GRADIENTS = [
                    "linear-gradient(135deg, #6366F1 0%, #06B6D4 100%)",
                    "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                    "linear-gradient(135deg, #0EA5E9 0%, #6366F1 100%)",
                    "linear-gradient(135deg, #14B8A6 0%, #6366F1 100%)",
                    "linear-gradient(135deg, #F472B6 0%, #8B5CF6 100%)",
                  ];
                  const gradientFor = (key) => {
                    if (!key) return STAFF_AVATAR_GRADIENTS[0];
                    let h = 0;
                    for (let i = 0; i < key.length; i += 1) {
                      h = (h * 31 + key.charCodeAt(i)) >>> 0;
                    }
                    return STAFF_AVATAR_GRADIENTS[
                      h % STAFF_AVATAR_GRADIENTS.length
                    ];
                  };

                  return (
                    <div className="space-y-6 max-w-6xl mx-auto">
                      {sortedDates.map((dateStr) => {
                        const [y, m, d] = dateStr.split("-").map(Number);
                        const dt = new Date(y, m - 1, d);
                        const heading = dt
                          .toLocaleDateString("en-US", {
                            weekday: "long",
                            day: "numeric",
                            month: "long",
                          })
                          .toUpperCase();

                        // Build staff columns for this day (only staff with
                        // at least 1 appointment that day). Stable order by
                        // earliest appointment time.
                        const byStaff = new Map();
                        groups[dateStr].forEach((a) => {
                          const sid = a.staff_id || "__unassigned__";
                          if (!byStaff.has(sid)) byStaff.set(sid, []);
                          byStaff.get(sid).push(a);
                        });
                        const columns = Array.from(byStaff.entries())
                          .map(([sid, appts]) => ({
                            sid: sid === "__unassigned__" ? null : sid,
                            appts: appts.sort((a, b) =>
                              (a.time || "") < (b.time || "") ? -1 : 1,
                            ),
                          }))
                          .sort(
                            (a, b) =>
                              (a.appts[0]?.time || "") <
                              (b.appts[0]?.time || "")
                                ? -1
                                : 1,
                          );

                        return (
                          <section
                            key={dateStr}
                            data-testid={`schedule-day-${dateStr}`}
                            className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 sm:p-5"
                          >
                            <h3
                              className="text-xs font-bold tracking-[0.18em] text-slate-500 mb-4 pb-3 border-b border-slate-200"
                              data-testid={`schedule-day-heading-${dateStr}`}
                            >
                              {heading}
                            </h3>

                            {/*
                              Per-staff columns.
                              Mobile (<sm): 1 column per staff stacked
                                            vertically — full-width blocks,
                                            no horizontal scroll.
                              ≥sm: side-by-side columns; horizontal scroll
                                   kicks in when more columns than fit.
                            */}
                            <div
                              className="flex flex-col sm:flex-row gap-4 sm:gap-4 sm:overflow-x-auto pb-2 sm:snap-none"
                              data-testid={`schedule-day-columns-${dateStr}`}
                            >
                              {columns.map(({ sid, appts }) => {
                                const meta = staffMeta(sid);
                                return (
                                  <div
                                    key={sid || "unassigned"}
                                    data-testid={`schedule-staff-col-${sid || "unassigned"}`}
                                    className="w-full sm:flex-shrink-0 sm:flex-1 sm:min-w-[200px] sm:max-w-[280px] sm:w-auto pb-3 sm:pb-0 sm:pr-4 sm:last:pr-0 border-b sm:border-b-0 sm:border-r border-slate-200 last:border-b-0 sm:last:border-r-0"
                                  >
                                    {/* Column header — avatar + name + title */}
                                    <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-slate-100">
                                      {meta.avatar_url ? (
                                        <img
                                          src={meta.avatar_url}
                                          alt={meta.name}
                                          className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                                        />
                                      ) : (
                                        <div
                                          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm"
                                          style={{
                                            backgroundImage: gradientFor(
                                              sid || meta.name,
                                            ),
                                          }}
                                        >
                                          {(meta.name || "?")
                                            .trim()
                                            .charAt(0)
                                            .toUpperCase() || "?"}
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-slate-900 truncate">
                                          {meta.name}
                                        </p>
                                        <p className="text-xs text-slate-400 truncate">
                                          {meta.title}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Stacked appointment cards */}
                                    <div className="flex flex-col gap-2.5">
                                      {appts.map((a) => {
                                        const c = colorFor(a.status);
                                        return (
                                          <button
                                            key={a.id}
                                            type="button"
                                            onClick={() => {
                                              setSelectedAppointment(a);
                                              setDetailsOpen(true);
                                            }}
                                            data-testid={`schedule-appt-${a.id}`}
                                            className="text-left rounded-2xl p-3.5 transition-all hover:shadow-md border"
                                            style={{
                                              backgroundColor: c.bg,
                                              borderColor: c.border,
                                            }}
                                          >
                                            <div className="flex items-center justify-between mb-1.5">
                                              <span className="text-xs font-medium text-slate-500">
                                                {formatTime(a.time)}
                                              </span>
                                            </div>
                                            <p className="font-bold text-slate-900 leading-tight truncate">
                                              {a.customer_name || "Walk-in"}
                                            </p>
                                            <p className="text-sm text-slate-500 mt-0.5 truncate">
                                              {a.service_name || "Service"}
                                            </p>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </section>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Month View */}
            {viewMode === "calendar" && view === "month" && (
              <>
                <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50/50">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <div
                        key={day}
                        className="p-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider border-r border-slate-200 last:border-r-0"
                      >
                        <span className="hidden sm:inline">{day}</span>
                        <span className="sm:hidden">{day[0]}</span>
                      </div>
                    ),
                  )}
                </div>

                <div className="grid grid-cols-7">
                  {getMonthDays().map(({ date, isCurrentMonth }, idx) => {
                    const isToday =
                      date.toDateString() === new Date().toDateString();
                    const dayAppointments = getAppointmentsForDate(date);
                    const dayClosed = isDayClosed(date);

                    return (
                      <div
                        key={idx}
                        className={`min-h-[80px] sm:min-h-[110px] p-2 border-r border-b border-slate-200 transition-all duration-150 ${
                          dayClosed && isCurrentMonth
                            ? "bg-slate-50 cursor-not-allowed"
                            : !isCurrentMonth
                              ? "bg-slate-50/50 cursor-pointer hover:bg-slate-100/50"
                              : "cursor-pointer hover:bg-sky-50/30"
                        }`}
                        onClick={() => {
                          if (dayClosed) {
                            const hours = getBusinessHoursForDate(date);
                            if (hours?.is_closed) {
                              toast.error("Business is closed on this day");
                              return;
                            }
                          }
                          handleSlotClick(date, "09:00");
                        }}
                      >
                        <div
                          className={`text-sm font-semibold mb-2 ${
                            isToday
                              ? "bg-sky-600 text-white w-7 h-7 rounded-full flex items-center justify-center shadow-sm"
                              : dayClosed && isCurrentMonth
                                ? "text-slate-300"
                                : isCurrentMonth
                                  ? "text-slate-800"
                                  : "text-slate-300"
                          }`}
                        >
                          {date.getDate()}
                        </div>
                        {dayClosed &&
                          isCurrentMonth &&
                          dayAppointments.length === 0 && (
                            <div className="text-[10px] text-slate-300 select-none font-medium uppercase tracking-wide">
                              Closed
                            </div>
                          )}
                        <div className="space-y-1">
                          {dayAppointments.slice(0, 2).map((appt, apptIdx) => (
                            <div
                              key={appt.id}
                              className={`${getAppointmentColor(appt.status)} text-[11px] px-2 py-1 rounded truncate cursor-pointer font-medium ${apptIdx > 0 ? "hidden sm:block" : ""}`}
                              onClick={(e) => handleEventClick(e, appt)}
                              data-testid={`calendar-event-${appt.id}`}
                            >
                              <span className="hidden sm:inline opacity-70 mr-1">
                                {appt.time}
                              </span>
                              {appt.customer_name}
                            </div>
                          ))}
                          {dayAppointments.length > 2 && (
                            <div className="text-[10px] text-sky-600 font-semibold pl-1 hover:underline cursor-pointer">
                              +{dayAppointments.length - 2} more
                            </div>
                          )}
                          {dayAppointments.length > 1 && (
                            <div className="text-[10px] text-sky-600 font-semibold pl-1 sm:hidden">
                              +{dayAppointments.length - 1} more
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Quick Stats Sidebar */}
        {showStats && (
          <div
            className="w-full lg:w-80 flex-shrink-0 space-y-4 hidden lg:block"
            data-testid="quick-stats-sidebar"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider">
                Quick Stats
              </h3>
              <Sparkles className="w-4 h-4 text-amber-500" />
            </div>

            {/* Today */}
            <div className="bg-gradient-to-br from-sky-50 to-white rounded-xl p-5 shadow-sm border border-sky-100/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      Today
                    </p>
                    <p className="text-2xl font-bold text-slate-900">
                      {todayAppointments.length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-green-600 to-green-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${todayAppointments.length > 0 ? (completedToday / todayAppointments.length) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-1.5 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  {completedToday} completed
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-red-400"></div>
                  {cancelledToday} cancelled
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-rose-400"></div>
                  {noShowToday} no-show
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                  {pendingToday} pending
                </span>
              </div>
            </div>

            {/* This Week */}
            <div className="bg-gradient-to-br from-green-50 to-white rounded-xl p-5 shadow-sm border border-green-100/50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                      This Week
                    </p>
                    <p className="text-2xl font-bold text-slate-900">
                      {weekAppointments.length}
                    </p>
                  </div>
                </div>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full bg-gradient-to-r from-green-400 to-green-300 rounded-full transition-all duration-500"
                  style={{
                    width: `${weekAppointments.length > 0 ? (completedWeek / weekAppointments.length) * 100 : 0}%`,
                  }}
                />
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                <span className="flex items-center gap-1.5 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-green-400"></div>
                  {completedWeek} completed
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-red-400"></div>
                  {cancelledWeek} cancelled
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-rose-400"></div>
                  {noShowWeek} no-show
                </span>
                <span className="flex items-center gap-1.5 text-slate-600">
                  <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                  {pendingWeek} pending
                </span>
              </div>
            </div>

            {/* Weekly Performance Chart */}
            <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
              <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-4">
                Weekly Activity
              </h4>
              <div className="flex items-end justify-between h-28 gap-1.5">
                {weeklyPerformance.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex flex-col items-center justify-end h-20 relative group">
                      <div className="absolute -top-5 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-[10px] px-2 py-1 rounded font-medium whitespace-nowrap">
                        {day.count} appointments
                      </div>
                      <div
                        className={`w-full max-w-[32px] rounded-t-md transition-all duration-300 ${
                          day.isToday
                            ? "bg-gradient-to-t from-sky-600 to-sky-400"
                            : "bg-gradient-to-t from-slate-300 to-slate-200"
                        }`}
                        style={{
                          height: `${Math.max((day.count / maxCount) * 72, 4)}px`,
                        }}
                      />
                    </div>
                    <span
                      className={`text-[10px] mt-2 font-semibold ${day.isToday ? "text-sky-600" : "text-slate-400"}`}
                    >
                      {day.day}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Staff Stats */}
            {staff.length > 0 && (
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-100">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-4">
                  Staff Overview
                </h4>
                <div className="space-y-3">
                  {staff.slice(0, 4).map((s, idx) => {
                    const staffAppts = weekAppointments.filter(
                      (a) => a.staff_id === s.id,
                    );
                    const colors = [
                      "from-sky-400 to-sky-600",
                      "from-emerald-400 to-emerald-600",
                      "from-amber-400 to-amber-600",
                      "from-rose-400 to-rose-600",
                    ];
                    return (
                      <div key={s.id} className="flex items-center gap-3">
                        <div
                          className={`w-9 h-9 rounded-lg bg-gradient-to-br ${colors[idx % 4]} flex items-center justify-center text-white text-sm font-semibold shadow-sm`}
                        >
                          {(s.name || s.email)?.[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">
                            {s.name || s.email?.split("@")[0]}
                          </p>
                          <p className="text-xs text-slate-400">
                            {staffAppts.length} this week
                          </p>
                        </div>
                        <div className="text-sm font-semibold text-slate-600">
                          {staffAppts.length}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─────────────────────────────────────────────
          Appointment Details Modal
      ───────────────────────────────────────────── */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="p-0 gap-0 max-w-[95vw] sm:max-w-lg w-full flex flex-col max-h-[100dvh] sm:max-h-[90vh] sm:rounded-2xl overflow-hidden border-0 shadow-2xl">
          <DialogDescription className="sr-only">
            View and manage appointment information
          </DialogDescription>

          {selectedAppointment && (
            <>
              {/* Pinned header — title left, close button handled by Radix (top-right) */}
              <div className="px-6 pt-6 pb-4 border-b border-slate-100 bg-white shrink-0">
                <DialogTitle className="text-xl font-bold text-slate-900 tracking-tight">
                  Appointment Details
                </DialogTitle>
                <div className="flex items-center gap-2 mt-3">
                  <span className={getStatusBadge(selectedAppointment.status)}>
                    {getStatusLabel(selectedAppointment.status)}
                  </span>
                  <span className="text-xs text-slate-400 font-mono bg-slate-50 px-2 py-1 rounded">
                    #{selectedAppointment.id?.slice(0, 8)}
                  </span>
                </div>
              </div>

              {/* Scrollable body */}
              <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4 bg-slate-50/50">
                {/* Customer card */}
                <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2 mb-3">
                    <User className="w-4 h-4 text-slate-400" />
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Customer
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 text-white flex items-center justify-center font-bold text-lg shrink-0 shadow-sm">
                      {selectedAppointment.customer_name?.[0]?.toUpperCase() ||
                        "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">
                        {selectedAppointment.customer_name}
                      </p>
                      {selectedAppointment.customer_email && (
                        <p className="text-xs text-slate-500 truncate flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {selectedAppointment.customer_email}
                        </p>
                      )}
                      {selectedAppointment.customer_phone && (
                        <p className="text-xs text-slate-500 flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {selectedAppointment.customer_phone}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Service + Schedule side by side */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Service
                    </p>
                    <p className="font-semibold text-slate-900 text-sm leading-snug">
                      {selectedAppointment.service_name}
                    </p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span>{selectedAppointment.duration} min</span>
                      <span className="font-semibold text-emerald-600">
                        $
                        {selectedAppointment.service_price?.toFixed(2) ?? "N/A"}
                      </span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Schedule
                    </p>
                    <p className="font-semibold text-slate-900 text-sm leading-snug">
                      {selectedAppointment.date
                        ? new Date(
                            selectedAppointment.date + "T00:00:00",
                          ).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : ""}
                    </p>
                    <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-500">
                      <Clock className="w-3.5 h-3.5 shrink-0" />
                      <span className="font-medium">
                        {selectedAppointment.time} –{" "}
                        {selectedAppointment.end_time}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Customer notes */}
                {selectedAppointment.notes && (
                  <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
                      Customer Notes
                    </p>
                    <p className="text-sm text-slate-700">
                      {selectedAppointment.notes}
                    </p>
                  </div>
                )}

                {/* Payment Actions */}
                <PaymentActions
                  appointment={selectedAppointment}
                  api={api}
                  onUpdate={() => {
                    fetchData();
                    setDetailsOpen(false);
                  }}
                />
              </div>

              {/* Pinned action footer */}
              <div className="shrink-0 px-6 py-4 border-t border-slate-100 bg-white">
                <div className="flex flex-col sm:flex-row gap-2">
                  {selectedAppointment.status === "booked" && (
                    <>
                      <Button
                        className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-xl shadow-sm"
                        onClick={() =>
                          handleStatusAndClose(
                            selectedAppointment.id,
                            "completed",
                          )
                        }
                        data-testid="cal-complete-btn"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Complete
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 h-11 text-sm font-semibold rounded-xl border-slate-200"
                        onClick={() =>
                          handleStatusAndClose(
                            selectedAppointment.id,
                            "no_show",
                          )
                        }
                        data-testid="cal-noshow-btn"
                      >
                        <Clock className="w-4 h-4 mr-2" />
                        No Show
                      </Button>
                    </>
                  )}
                  {selectedAppointment.status === "pending" && (
                    <Button
                      className="flex-1 h-11 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold rounded-xl shadow-sm"
                      onClick={() =>
                        updateAppointmentStatus(
                          selectedAppointment.id,
                          "booked",
                        )
                      }
                      data-testid="cal-confirm-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Confirm Appointment
                    </Button>
                  )}
                  {selectedAppointment.status === "completed" && (
                    <Button
                      variant="outline"
                      className="flex-1 h-11 text-sm font-semibold rounded-xl border-slate-200"
                      onClick={() => generateInvoice(selectedAppointment.id)}
                      disabled={generatingInvoice}
                      data-testid="cal-invoice-btn"
                    >
                      {generatingInvoice ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Generate Invoice
                        </>
                      )}
                    </Button>
                  )}
                  {selectedAppointment.status !== "cancelled" &&
                    selectedAppointment.status !== "completed" && (
                      <Button
                        variant="outline"
                        className="flex-1 h-11 text-sm font-semibold text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
                        onClick={() =>
                          handleStatusAndClose(
                            selectedAppointment.id,
                            "cancelled",
                          )
                        }
                        data-testid="cal-cancel-btn"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ─────────────────────────────────────────────
          Walk-in Appointment Modal
      ───────────────────────────────────────────── */}
      <Dialog open={showWalkInModal} onOpenChange={setShowWalkInModal}>
        <DialogContent className="p-0 gap-0 max-w-[95vw] sm:max-w-lg w-full flex flex-col max-h-[100dvh] sm:max-h-[90vh] sm:rounded-2xl overflow-hidden border-0 shadow-2xl">
          <DialogDescription className="sr-only">
            Create a new walk-in appointment
          </DialogDescription>

          {/* Pinned header */}
          <div className="flex items-center gap-4 px-6 py-5 border-b border-slate-100 bg-white shrink-0">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-400 to-sky-600 flex items-center justify-center shrink-0 shadow-sm">
              <Plus className="w-5 h-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-slate-900 tracking-tight">
                New Walk-in
              </DialogTitle>
              <p className="text-sm text-slate-500">
                Create an appointment for a walk-in customer
              </p>
            </div>
          </div>

          {/* Scrollable form body */}
          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5 bg-slate-50/50">
            {/* Date & Time row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Date
                </label>
                <input
                  type="date"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 transition-all"
                  value={walkInData.date}
                  onChange={(e) => {
                    const date = e.target.value;
                    const workingStaff = getStaffWorkingOnDate(date);
                    const selectedStaffIsWorking = workingStaff.some(
                      (staffMember) =>
                        staffMember.id === walkInData.staff_id,
                    );
                    setWalkInData({
                      ...walkInData,
                      date,
                      staff_id: selectedStaffIsWorking
                        ? walkInData.staff_id
                        : workingStaff[0]?.id || "",
                    });
                  }}
                  data-testid="walkin-date"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Time
                </label>
                <Select
                  value={walkInData.time}
                  onValueChange={(v) =>
                    setWalkInData({ ...walkInData, time: v })
                  }
                >
                  <SelectTrigger
                    className="h-12 rounded-xl text-sm border-slate-200 bg-white"
                    data-testid="walkin-time"
                  >
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Customer details section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-slate-400" />
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Customer Details
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 placeholder:text-slate-400 transition-all"
                  placeholder="Enter customer name"
                  value={walkInData.customer_name}
                  onChange={(e) =>
                    setWalkInData({
                      ...walkInData,
                      customer_name: e.target.value,
                    })
                  }
                  data-testid="walkin-name"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  inputMode="tel"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 placeholder:text-slate-400 transition-all"
                  placeholder="Enter phone number"
                  value={walkInData.customer_phone}
                  onChange={(e) =>
                    setWalkInData({
                      ...walkInData,
                      customer_phone: e.target.value,
                    })
                  }
                  data-testid="walkin-phone"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Email{" "}
                  <span className="text-xs text-slate-400 font-normal">
                    (Optional)
                  </span>
                </label>
                <input
                  type="text"
                  inputMode="email"
                  className="w-full h-12 px-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 placeholder:text-slate-400 transition-all"
                  placeholder="Enter email (optional)"
                  value={walkInData.customer_email}
                  onChange={(e) =>
                    setWalkInData({
                      ...walkInData,
                      customer_email: e.target.value,
                    })
                  }
                  data-testid="walkin-email"
                />
              </div>
            </div>

            {/* Booking details section */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-400" />
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Booking Details
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700">
                  Service <span className="text-red-500">*</span>
                </label>
                <Select
                  value={walkInData.service_id}
                  onValueChange={(v) =>
                    setWalkInData({ ...walkInData, service_id: v })
                  }
                >
                  <SelectTrigger
                    className="h-12 rounded-xl text-sm border-slate-200 bg-white"
                    data-testid="walkin-service"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} ({s.duration} min) — $
                        {s.price?.toFixed(2) ?? s.price}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {staff.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-700">
                    Assign Staff{" "}
                    <span className="text-xs text-slate-400 font-normal">
                      (Optional)
                    </span>
                  </label>
                  <Select
                    value={walkInData.staff_id}
                    onValueChange={(v) =>
                      setWalkInData({ ...walkInData, staff_id: v })
                    }
                  >
                    <SelectTrigger
                      className="h-12 rounded-xl text-sm border-slate-200 bg-white"
                      data-testid="walkin-staff"
                    >
                      <SelectValue placeholder="Any available staff" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableWalkInStaff.length > 0 ? (
                        availableWalkInStaff.map((s) => (
                          <SelectItem key={s.id} value={s.id}>
                            {s.name || s.email}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-staff" disabled>
                          No staff working this day
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          {/* Pinned footer */}
          <div className="shrink-0 px-6 py-4 border-t border-slate-100 bg-white">
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="outline"
                className="flex-1 h-12 text-sm font-semibold rounded-xl border-slate-200"
                onClick={() => setShowWalkInModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 h-12 bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold rounded-xl shadow-sm"
                onClick={handleSaveWalkIn}
                disabled={savingWalkIn}
                data-testid="walkin-save-btn"
              >
                {savingWalkIn ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Appointment
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default CalendarPage;
